import { useEffect, useMemo, useState } from "react";
import type { ViewerSettings } from "../../types/settings";
import { withOpacity } from "../../utils/colorUtils";
export interface StepVisibilityConfirmPopoverPoint {
  x: number;
  y: number;
}

export interface StepVisibilityConfirmPopoverStep {
  name: string;
  startFrame: number;
  endFrame: number;
}

export interface StepVisibilityCurrentRule {
  included: boolean;
  stepName: string;
  startFrame: number;
  endFrame: number;
}

export interface StepVisibilityConfirmPopoverProps {
  isOpen: boolean;
  pointer: StepVisibilityConfirmPopoverPoint | null;
  objectLabel: string | null;
  step: StepVisibilityConfirmPopoverStep | null;
  currentRule: StepVisibilityCurrentRule | null;
  onInclude: () => void;
  onExclude: () => void;
  onClose: () => void;
  language?: ViewerSettings["language"];
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  accentColor?: string;
  fontFamily?: string;
  [key: string]: unknown;
}

interface DragPoint {
  x: number;
  y: number;
}

const DEFAULT_OFFSET = 14;
const MIN_LEFT = 16;
const MIN_TOP = 16;

function getCopy(language: ViewerSettings["language"] = "en") {
  switch (language) {
    case "es":
      return {
        objectLabel: "Objeto",
        stepLabel: "Paso",
        currentRule: "Regla actual",
        include: "Incluir en el paso",
        exclude: "Excluir del paso",
        close: "Cerrar",
      };
    case "fr":
      return {
        objectLabel: "Objet",
        stepLabel: "Étape",
        currentRule: "Règle actuelle",
        include: "Inclure dans l'étape",
        exclude: "Exclure de l'étape",
        close: "Fermer",
      };
    case "de":
      return {
        objectLabel: "Objekt",
        stepLabel: "Schritt",
        currentRule: "Aktuelle Regel",
        include: "In Schritt aufnehmen",
        exclude: "Aus Schritt ausschließen",
        close: "Schließen",
      };
    case "zh":
      return {
        objectLabel: "对象",
        stepLabel: "步骤",
        currentRule: "当前规则",
        include: "包含到步骤中",
        exclude: "从步骤中排除",
        close: "关闭",
      };
    case "en":
    default:
      return {
        objectLabel: "Object",
        stepLabel: "Step",
        currentRule: "Current rule",
        include: "Include in Step",
        exclude: "Exclude from Step",
        close: "Close",
      };
  }
}

function formatFrameRange(step: StepVisibilityConfirmPopoverStep | null): string {
  if (!step) return "";
  return `${step.name} (${step.startFrame}\u2013${step.endFrame})`;
}

function formatCurrentRule(rule: StepVisibilityCurrentRule | null): string {
  if (!rule) return "No rule set";

  return `${rule.included ? "Included" : "Excluded"} at ${rule.stepName}`;
}

export function StepVisibilityConfirmPopover({
  isOpen,
  pointer,
  objectLabel,
  step,
  currentRule,
  onInclude,
  onExclude,
  onClose,
  language = "en",
  backgroundColor = "rgba(30, 30, 30, 0.9)",
  backdropBlur = "24px",
  borderColor = "rgba(255, 255, 255, 0.12)",
  boxShadow = "0 8px 32px rgba(0, 0, 0, 0.4)",
  primaryTextColor = "#ffffff",
  secondaryTextColor = "rgba(255, 255, 255, 0.72)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
  ...rest
}: StepVisibilityConfirmPopoverProps) {
  const copy = useMemo(() => getCopy(language), [language]);
  const [position, setPosition] = useState<DragPoint>(() => ({
    x: (pointer?.x ?? 120) + DEFAULT_OFFSET,
    y: (pointer?.y ?? 180) + DEFAULT_OFFSET,
  }));
  const [dragOffset, setDragOffset] = useState<DragPoint | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPosition({
      x: (pointer?.x ?? 120) + DEFAULT_OFFSET,
      y: (pointer?.y ?? 180) + DEFAULT_OFFSET,
    });
  }, [isOpen, pointer?.x, pointer?.y]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        onInclude();
        return;
      }

      if (event.key.toLowerCase() === "n") {
        event.preventDefault();
        onExclude();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, onExclude, onInclude]);

  useEffect(() => {
    if (!isOpen || !dragOffset) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      setPosition({
        x: Math.max(MIN_LEFT, event.clientX - dragOffset.x),
        y: Math.max(MIN_TOP, event.clientY - dragOffset.y),
      });
    };

    const handleMouseUp = () => {
      setDragOffset(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragOffset, isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <section
      {...rest}
      aria-label="Step visibility confirmation"
      role="dialog"
      data-testid="step-visibility-popover"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 120,
        width: "min(24rem, calc(100vw - 2rem))",
        borderRadius: "1.25rem",
        border: `1px solid ${borderColor}`,
        backgroundColor,
        backdropFilter: `blur(${backdropBlur})`,
        WebkitBackdropFilter: `blur(${backdropBlur})`,
        boxShadow,
        color: primaryTextColor,
        fontFamily,
        overflow: "hidden",
      }}
    >
      <header
        data-testid="step-visibility-popover-drag-handle"
        onMouseDown={(event) => {
          event.preventDefault();
          setDragOffset({
            x: event.clientX - position.x,
            y: event.clientY - position.y,
          });
        }}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "1rem",
          padding: "1rem 1rem 0.875rem",
          cursor: "grab",
          userSelect: "none",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: "0.7rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: secondaryTextColor,
            }}
          >
            {copy.objectLabel}
          </p>
          <h2
            style={{
              margin: "0.25rem 0 0",
              fontSize: "1rem",
              fontWeight: 600,
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {objectLabel || "Unknown object"}
          </h2>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label={copy.close}
          style={{
            border: "none",
            background: "transparent",
            color: secondaryTextColor,
            cursor: "pointer",
            fontSize: "1rem",
            lineHeight: 1,
            padding: "0.25rem",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </header>

      <div style={{ padding: "0 1rem 1rem" }}>
        <div
          style={{
            display: "grid",
            gap: "0.75rem",
            padding: "0.875rem",
            borderRadius: "1rem",
            background: "rgba(255, 255, 255, 0.04)",
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: "0.7rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: secondaryTextColor,
              }}
            >
              {copy.stepLabel}
            </p>
            <p
              style={{
                margin: "0.35rem 0 0",
                fontSize: "0.95rem",
                color: primaryTextColor,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatFrameRange(step)}
            </p>
          </div>

          <div>
            <p
              style={{
                margin: 0,
                fontSize: "0.7rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: secondaryTextColor,
              }}
            >
              {copy.currentRule}
            </p>
            <p
              style={{
                margin: "0.35rem 0 0",
                fontSize: "0.9rem",
                color: primaryTextColor,
              }}
            >
              {copy.currentRule}: {formatCurrentRule(currentRule)}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.75rem",
            marginTop: "0.875rem",
          }}
        >
          <button
            type="button"
            onClick={onInclude}
            style={{
              minHeight: "2.75rem",
              borderRadius: "0.875rem",
              border: `1px solid ${withOpacity(accentColor, 0.35)}`,
              background: withOpacity(accentColor, 0.16),
              color: primaryTextColor,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {copy.include}
          </button>

          <button
            type="button"
            onClick={onExclude}
            style={{
              minHeight: "2.75rem",
              borderRadius: "0.875rem",
              border: "1px solid rgba(255, 255, 255, 0.14)",
              background: "rgba(255, 255, 255, 0.06)",
              color: primaryTextColor,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {copy.exclude}
          </button>
        </div>
      </div>
    </section>
  );
}
